import { giphyDarkestGrey, giphyLightGrey, giphyLightestGrey } from '@giphy/colors'
import { uniqueId } from 'lodash'
import { useContext, useRef } from 'react'
import { CopilotUploadPreset } from 'shared/types/copilot'
import styled from 'styled-components'
import { ControlSize } from 'types'
import { Button as ButtonComponent, IconComponent, PortalTooltip, useTooltip } from 'ui'
import { CopilotContext } from '../context'
import { CopilotAlertContext } from '../contexts/alert'
import { CopilotApiContext } from '../contexts/api'
import { CopilotDropdownContext } from '../contexts/dropdown'
import { CopilotPresetOption, copilotPresetOptions } from '../types'
import { iconFromPresetOption, iconScaleFromPresetOption, labelFromPresetOption } from '../utils/preset-option'
import { Avatar as PresetAvatar, PresetButton, PresetInfo } from './style'

const Avatar = styled(PresetAvatar)`
    margin-right: 0.5em;
`

const Button = styled(ButtonComponent)`
    color: ${giphyLightGrey};
    font-weight: normal;
    text-align: left;
    transition: color 0.1s ease-out;
    width: 100%;

    &:hover {
        color: ${giphyLightestGrey};
    }
`

const Lock = styled.div`
    display: inline-block;
    margin: 0 4px;
`

const Icon = styled(IconComponent)`
    opacity: 0.3;
`

const Menu = styled.div`
    padding: 10px;
`

const Username = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`

type Props = {
    className?: string
    preset: CopilotUploadPreset
    size: ControlSize
}

const CopilotPresetMenu = ({ className, preset, size }: Props) => {
    const idRef = useRef<string>(uniqueId('copilot-sidebar-preset-menu'))
    const { copyPresetToClipboard } = useContext(CopilotContext)
    const { showAlert } = useContext(CopilotAlertContext)
    const { closeDropdown } = useContext(CopilotDropdownContext)
    const { deletePreset, duplicatePreset, renamePreset, updatePreset, removeImportedPreset } =
        useContext(CopilotApiContext)
    const avatarUrl = preset.author.avatar_url

    const { isOpen, listeners, closeTooltip, setNodeRef } = useTooltip({
        color: giphyDarkestGrey,
        direction: 'right',
        id: idRef.current,
        width: 230,
    })

    const availableOptions = copilotPresetOptions.filter((option) => {
        switch (option) {
            case 'delete':
            case 'update':
            case 'rename':
                return !preset.is_locked
            case 'remove':
                return preset.is_locked
            default:
                return true
        }
    })

    async function onButtonClick(option: CopilotPresetOption) {
        closeTooltip()

        if (option !== 'copy') {
            closeDropdown()
        }

        switch (option) {
            case 'copy':
                copyPresetToClipboard(preset.id)
                break
            case 'delete':
                await showAlert('delete')
                deletePreset(preset)
                break
            case 'duplicate':
                const duplicateName = await showAlert('duplicate', preset)
                duplicatePreset(preset, duplicateName)
                break
            case 'remove':
                await showAlert('remove')
                removeImportedPreset(preset)
                break
            case 'rename':
                const newName = await showAlert('rename', preset)
                renamePreset(preset, newName)
                break
            case 'update':
                await showAlert('update')
                updatePreset(preset)
                break
            default:
                break
        }
    }

    return (
        <>
            <PresetButton {...listeners} color={giphyLightGrey} icon="ellipsis" inverted ref={setNodeRef} size={size} />
            <PortalTooltip isOpen={isOpen}>
                <Menu className={className}>
                    <PresetInfo $size="small">
                        {avatarUrl ? <Avatar src={avatarUrl} alt="" /> : null}
                        <Username>@{preset.author.username}</Username>
                        {preset.is_locked && (
                            <Lock title="This preset can only be modified by the author.">
                                <IconComponent color={giphyLightGrey} icon="lock" height={14} width={14} />
                            </Lock>
                        )}
                    </PresetInfo>
                    {availableOptions.map((option) => {
                        const iconSize = Math.round(18 * iconScaleFromPresetOption(option))

                        return (
                            <Button
                                color="transparent"
                                icon={<Icon icon={iconFromPresetOption(option)} height={iconSize} width={iconSize} />}
                                key={`copilot-sidebar-menu-item-${option}`}
                                size="medium"
                                onClick={() => onButtonClick(option)}
                            >
                                {labelFromPresetOption(option)}
                            </Button>
                        )
                    })}
                </Menu>
            </PortalTooltip>
        </>
    )
}

export default CopilotPresetMenu
